<template>
  <a class="profile-card" target="_blank" :href="linkProfile">
    <CloseBigIcon v-if="close" class="profile-card__close" @click.prevent="emits('action-close', card.id)" />
    <template v-if="loading">
      <div class="profile-card-photo-wrapper">
        <div class="profile-card__photo-skeleton skeleton"></div>
      </div>
      <div class="profile-card__metro-skeleton skeleton"></div>
      <div class="profile-card__name-skeleton skeleton"></div>
      <div class="profile-card__price-skeleton skeleton"></div>
    </template>
    <template v-else>
      <div class="profile-card-photo-wrapper">
        <div v-if="loading" class="profile-card__photo-skeleton skeleton"></div>
        <img v-else-if="getFirstPhoto && !loading" class="profile-card__photo" :src="getFirstPhoto" alt="photo">
        <div v-else class="profile-card__photo-stub stub">
          <ProfileIcon class="stub__icon" />
          <span class="stub__text">Добавить превью</span>
        </div>
      </div>
      <p class="profile-card__metro">
        {{ getMetroName }}
      </p>
      <p class="profile-card__name">
        {{ card.name }}
      </p>
      <p class="profile-card__price">
        {{ card.priceInfo }}
      </p>
    </template>
  </a>
</template>

<script setup lang="ts">
import { computed, ComputedRef, PropType } from "vue";
import ProfileCardInterface from "@/common/types/ProfileCardInterface";
import ProfileIcon from "@/common/icons/ProfileIcon.vue";
import CloseBigIcon from "./icons/CloseBigIcon.vue";
import {cityPathMap} from "@/searchSection/types/ECities";

const props = defineProps({
  card: {
    type: Object as PropType<ProfileCardInterface>,
    required: true,
  },
  close: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits(['action-close'])

const linkProfile = computed<string>(() => {
  let cityUrl = ''
  if (props.card?.cityId !== 1) {
    cityUrl = `/${cityPathMap[props.card?.cityId]}`
  }
  return `${cityUrl}/escort/${props.card.id}`
})

const getMetroName: ComputedRef<string> = computed(() => {
  if (props.card.metroStations.length !== 0) {
    return props.card?.metroStations[0].name;
  }
  return '';
})

const getMetroColor: ComputedRef<string> = computed(() => {
  if (props.card.metroStations.length !== 0) {
    return '#' + (props.card?.metroStations[0].color || 'fff');
  }
  return '';
})

const getFirstPhoto: ComputedRef<string | undefined> = computed(() => {  
  if (props.card.photosSmall.length !== 0) {
    if (props.card.photosSmall[0].includes('blob')) {
      return props.card.photosSmall[0];
    }

    return '/assets/uploads/photos/' + props.card.photosSmall[0];
  }

  return undefined;
})
</script>

<style scoped lang="scss">
.profile-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid #1E2023;
  border-radius: 16px;
  padding: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  line-height: 16px;
  position: relative;
  height: min-content;

  &__close {
    position: absolute;
    top: 14px;
    right: 14px;
    
    :deep(path) {
      fill-opacity: 1;
      &:first-child {
        fill: rgba(57, 62, 69, 1);
      }
      &:last-child {
        fill: rgba(255, 255, 255, 0.25);
      }
    }      
  }

  &-photo-wrapper {
    overflow: hidden;
    width: 100%;
    border-radius: 12px;

    .stub {
      background-color: rgba(21, 22, 24, 1);
      width: 100%;
      aspect-ratio: 0.7;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      svg {
        width: 46%;
        height: auto;
      }

      &__icon {
        margin-bottom: 20px;
      }

      &__text {
        font-weight: 700;
        font-size: 20px;
        line-height: 22px;
        color: rgba(146, 153, 165, 1);
        max-width: 158px;
        text-align: center;
      }
    }
  }

  &__photo {
    aspect-ratio: 0.7;
    max-width: unset;
    object-fit: cover;
    margin-bottom: 10px;
  }

  &__metro,
  &__price {
    color: #737880;
  }

  &__metro {
    position: relative;
    padding-left: 12px;

    &::after {
      position: absolute;
      content: '';
      top: 50%;
      left: 0;
      transform: translateY(-50%);
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: v-bind(getMetroColor);
    }
  }

  &__name {
    color: #fff;
  }

  &__photo-skeleton {
    width: 100%;
    aspect-ratio: 0.7;
    border-radius: 12px;
  }

  &__metro-skeleton {
    width: 60%;
    height: 16px;
    border-radius: 4px;
  }

  &__name-skeleton {
    width: 80%;
    height: 16px;
    border-radius: 4px;
  }

  &__price-skeleton {
    width: 40%;
    height: 16px;
    border-radius: 4px;
  }
}

.skeleton {
  background: linear-gradient(110deg, #1b1f28 8%, #373c4b 18%, #1b1f28 33%);
  background-size: 200% 100%;
  animation: shine 1.5s linear infinite;
}

@keyframes shine {
  to {
    background-position-x: -200%;
  }
}
</style>